/**
 * The bell: what you missed while you were elsewhere.
 *
 * Mentions and task hand-offs arrive over the socket the moment they happen
 * and are waiting in `welcome` when you return, so being offline never loses
 * one. Reading is all-or-nothing on open — per-item read state is more
 * bookkeeping than a list this short deserves.
 */

import type { Notification } from '@virtual-office/shared';

import type { Store } from '../state.js';
import { $, clear, el, relativeTime } from './dom.js';

/**
 * One line describing why somebody is being told.
 *
 * Shared by the panel and the toast so the two never disagree about what just
 * happened. The body already carries the task number and title, so the
 * heading only has to say who and why.
 */
export function describeNotification(notification: Notification): string {
  const who = notification.actorName;
  switch (notification.kind) {
    case 'task-assigned':
      return `${who} assigned you a task`;
    case 'task-approval':
      return `${who} needs your approval`;
    case 'task-approved':
      return `${who} approved your task`;
    case 'task-unblocked':
      return `${who} cleared your blocker`;
    case 'mention':
    default:
      // "Mei tagged @design" reads better than "you were mentioned", because
      // the group is usually why you care.
      return notification.via ? `${who} tagged @${notification.via}` : `${who} mentioned you`;
  }
}

export class NotificationsPanel {
  private readonly panel = $('notifications');
  private readonly list = $('notifications-list');
  private readonly bell = $('bell');
  private readonly badge = $('bell-count');

  constructor(
    private readonly store: Store,
    private readonly onRead: () => void,
    private readonly onOpenTask: (taskId: string) => void,
  ) {
    $('notifications-close').addEventListener('click', () => this.close());
    this.store.on('notifications', () => {
      this.renderBadge();
      if (this.isOpen) this.renderList();
    });
    this.renderBadge();
  }

  get isOpen(): boolean {
    return !this.panel.hidden;
  }

  open(): void {
    this.panel.hidden = false;
    this.renderList();

    // Marked read on open rather than per item: if you looked at the list, you
    // have seen them.
    if (this.store.unreadCount > 0) {
      this.store.markAllRead();
      this.onRead();
      this.renderBadge();
    }
  }

  close(): void {
    this.panel.hidden = true;
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  private renderBadge(): void {
    const count = this.store.unreadCount;
    this.badge.textContent = count > 99 ? '99+' : String(count);
    this.badge.hidden = count === 0;
    this.bell.classList.toggle('has-unread', count > 0);
  }

  private renderList(): void {
    clear(this.list);
    if (this.store.notifications.length === 0) {
      this.list.append(el('p', { className: 'empty', text: 'Nothing yet.' }));
      return;
    }
    for (const notification of this.store.notifications) {
      this.list.append(this.renderOne(notification));
    }
  }

  private renderOne(notification: Notification): HTMLElement {
    const row = el('div', { className: 'notification' });
    if (notification.readAt === null) row.classList.add('unread');

    row.append(
      el('div', { className: 'notification-head', text: describeNotification(notification) }),
      el('div', { className: 'notification-body', text: notification.body }),
      el('div', {
        className: 'notification-meta',
        text: `${notification.context} · ${relativeTime(notification.createdAt)}`,
      }),
    );

    // A task notification is only useful if it gets you to the task.
    const taskId = notification.taskId;
    if (taskId !== null) {
      row.classList.add('clickable');
      row.addEventListener('click', () => {
        this.close();
        this.onOpenTask(taskId);
      });
    }
    return row;
  }
}
